import { useCallback, useEffect, useState } from "react";
import api, { apiErr, downloadFile } from "@/lib/api";
import { STANDARDS } from "@/constants/testIds";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { ProvenanceList } from "@/components/ProvenancePanel";
import {
  Download, CheckCircle2, AlertTriangle, ShieldAlert, Lock, Database, ChevronDown,
  Scale, Settings2, PenLine, Building2,
} from "lucide-react";

const GRADE_TONE = {
  good: "bg-emerald-100 text-emerald-900 border-emerald-300",
  warn: "bg-amber-100 text-amber-900 border-amber-300",
  bad: "bg-rose-100 text-rose-900 border-rose-300",
};
const scoreColor = (s) =>
  s == null ? "text-slate-400" : s >= 7.5 ? "text-forest-mid" : s >= 6 ? "text-amber-600" : "text-rose-600";

function KpiRow({ periodId, areaKey, k, canEdit, navigate, onChange }) {
  const [score, setScore] = useState(k.override ?? "");
  const [note, setNote] = useState(k.note || "");
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState(false);
  const [prov, setProv] = useState(null);

  const dirty = String(score) !== String(k.override ?? "") || note !== (k.note || "");

  const save = async () => {
    setBusy(true);
    try {
      await api.post(`/score/periods/${periodId}/lias/kpi/${k.key}`, {
        score: score === "" ? null : Number(score), note,
      });
      toast.success("Score updated");
      await onChange();
    } catch (e) { toast.error(apiErr(e)); }
    setBusy(false);
  };

  const signoff = async () => {
    try {
      const r = await api.post(`/score/periods/${periodId}/lias/kpi/${k.key}/signoff`);
      toast.success(r.data.signed_off ? "KPI signed off" : "Sign-off removed");
      await onChange();
    } catch (e) { toast.error(apiErr(e)); }
  };

  const toggleEvidence = async () => {
    const nv = !open;
    setOpen(nv);
    if (nv && prov === null) {
      const entries = await Promise.all((k.roots_kpis || []).map(async (rk) => {
        try { const r = await api.get(`/score/periods/${periodId}/entries/${rk.code}/provenance`); return [rk.code, r.data]; }
        catch { return [rk.code, null]; }
      }));
      setProv(Object.fromEntries(entries));
    }
  };

  const openRecord = (l) => {
    const lk = l.link || {};
    if (lk.area === "setup") navigate(`/setup?tab=${lk.tab}&record=${lk.record_id}`);
    else navigate(`/project?tab=${lk.tab || "work-orders"}`);
  };

  return (
    <div className="rounded-lg border border-sage-light bg-white p-3" data-testid={STANDARDS.lias.kpi(k.key)}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="text-sm font-semibold text-forest-dark">
            {k.name}
            {k.focus && <span className="ml-1.5 rounded bg-sage-surface px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide text-forest-mid">{k.focus} focus</span>}
          </p>
          <p className="mt-0.5 text-[11px] leading-relaxed text-muted-foreground">Target: {k.target}</p>
        </div>
        <div className="shrink-0 text-right">
          <p className={`font-heading text-2xl font-bold ${scoreColor(k.score)}`}>{k.score == null ? "—" : k.score}<span className="text-xs font-normal text-muted-foreground">/10</span></p>
          <p className="text-[10px] text-muted-foreground">proposed {k.proposed == null ? "—" : k.proposed}</p>
        </div>
      </div>

      <div className="mt-2 flex flex-wrap items-center gap-2">
        {canEdit ? (
          <>
            <Input
              type="number" min="0" max="10" step="1" value={score}
              onChange={(e) => setScore(e.target.value)}
              placeholder={k.proposed == null ? "—" : String(k.proposed)}
              className="h-8 w-20 text-sm" data-testid={STANDARDS.lias.kpiScore(k.key)}
            />
            <Input
              value={note} onChange={(e) => setNote(e.target.value)} placeholder="Assessor note (optional)"
              className="h-8 flex-1 min-w-[140px] text-sm" data-testid={STANDARDS.lias.kpiNote(k.key)}
            />
            <Button size="sm" className="h-8 bg-forest-mid text-xs hover:bg-forest-dark" disabled={!dirty || busy}
              onClick={save} data-testid={STANDARDS.lias.kpiSave(k.key)}>Save</Button>
            <Button size="sm" variant={k.signed_off ? "default" : "outline"}
              className={`h-8 text-xs ${k.signed_off ? "bg-emerald-600 hover:bg-emerald-700" : ""}`}
              onClick={signoff} data-testid={STANDARDS.lias.kpiSignoff(k.key)}>
              <CheckCircle2 className="mr-1 h-3.5 w-3.5" /> {k.signed_off ? "Signed" : "Sign off"}
            </Button>
          </>
        ) : (
          <>
            {k.override != null && <span className="rounded bg-sage-surface px-1.5 py-0.5 text-[11px] text-forest-dark">assessor set {k.override}/10</span>}
            {k.note && <span className="text-[11px] italic text-muted-foreground">“{k.note}”</span>}
            {k.signed_off && <span className="inline-flex items-center gap-1 rounded bg-emerald-100 px-1.5 py-0.5 text-[11px] font-medium text-emerald-800"><CheckCircle2 className="h-3 w-3" /> Signed</span>}
          </>
        )}
      </div>
      {canEdit && k.signed_off && k.signed_by_name && (
        <p className="mt-1 text-[10px] text-emerald-700">Signed off by {k.signed_by_name}</p>
      )}

      <button onClick={toggleEvidence} className="mt-2 flex items-center gap-1 text-[11px] font-semibold text-forest-mid hover:text-forest-dark"
        data-testid={STANDARDS.lias.kpiEvidence(k.key)}>
        <ChevronDown className={`h-3.5 w-3.5 transition-transform ${open ? "rotate-180" : ""}`} /> {open ? "Hide" : "ROOTS evidence"} ·
        {(k.roots_kpis || []).map((r) => ` ${r.code}${r.score != null ? ` (${r.score})` : ""}`).join(" ")}
      </button>
      {open && (
        <div className="mt-2 space-y-2 border-t border-sage-light pt-2">
          {(k.roots_kpis || []).map((r) => (
            <div key={r.code} className="rounded-md bg-sage-surface/50 p-2">
              <p className="mb-1 flex items-center gap-1.5 text-[11px] font-semibold text-forest-dark">
                <Database className="h-3 w-3 text-forest-mid" /> <span className="font-mono">{r.code}</span> {r.name}
                {r.score != null && <span className="text-muted-foreground">· ROOTS score {r.score}/100</span>}
              </p>
              <ProvenanceList prov={prov?.[r.code]} onOpenRecord={openRecord} />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function ConfigEditor({ periodId, contractId, config, onChange }) {
  const [open, setOpen] = useState(false);
  const [bonus, setBonus] = useState(config.bonus_pct);
  const [c, setC] = useState(config.grade_c_deduction_pct);
  const [d, setD] = useState(config.grade_d_deduction_pct);

  const save = async () => {
    try {
      await api.patch(`/score/contracts/${contractId}/lias-config`, {
        bonus_pct: Number(bonus), grade_c_deduction_pct: Number(c), grade_d_deduction_pct: Number(d),
      });
      toast.success("Payment framework updated for this contract");
      setOpen(false);
      await onChange();
    } catch (e) { toast.error(apiErr(e)); }
  };

  return (
    <div className="rounded-lg border border-dashed border-sage-light p-3">
      <button onClick={() => setOpen(!open)} className="flex items-center gap-1.5 text-xs font-semibold text-forest-mid hover:text-forest-dark" data-testid={STANDARDS.lias.configBtn}>
        <Settings2 className="h-3.5 w-3.5" /> {open ? "Hide" : "Configure"} payment framework (bonus / deductions)
      </button>
      {open && (
        <div className="mt-3 flex flex-wrap items-end gap-3">
          <label className="text-[11px] text-muted-foreground">Grade A bonus %
            <Input type="number" min="0" step="1" value={bonus} onChange={(e) => setBonus(e.target.value)} className="mt-1 h-8 w-24 text-sm" data-testid={STANDARDS.lias.configBonus} />
          </label>
          <label className="text-[11px] text-muted-foreground">Grade C deduction %
            <Input type="number" min="0" step="1" value={c} onChange={(e) => setC(e.target.value)} className="mt-1 h-8 w-24 text-sm" data-testid={STANDARDS.lias.configC} />
          </label>
          <label className="text-[11px] text-muted-foreground">Grade D deduction %
            <Input type="number" min="0" step="1" value={d} onChange={(e) => setD(e.target.value)} className="mt-1 h-8 w-24 text-sm" data-testid={STANDARDS.lias.configD} />
          </label>
          <Button size="sm" className="h-8 bg-forest-mid text-xs hover:bg-forest-dark" onClick={save} data-testid={STANDARDS.lias.configSave}>Save</Button>
        </div>
      )}
    </div>
  );
}

function JointSignoff({ periodId, data, canEdit, canReopen, onChange }) {
  const [contractor, setContractor] = useState("");
  const [comments, setComments] = useState("");
  const signed = data.status === "signed";
  const so = data.signoff || {};

  const sign = async () => {
    try {
      await api.post(`/score/periods/${periodId}/lias/signoff`, { contractor_name: contractor, comments });
      toast.success("Scorecard signed off — now frozen");
      await onChange();
    } catch (e) { toast.error(apiErr(e)); }
  };
  const reopen = async () => {
    try {
      await api.post(`/score/periods/${periodId}/lias/reopen`);
      toast("Scorecard reopened to draft");
      await onChange();
    } catch (e) { toast.error(apiErr(e)); }
  };

  return (
    <div className="rounded-lg border border-sage-light bg-sage-surface p-4">
      <p className="flex items-center gap-1.5 text-sm font-semibold text-forest-dark"><PenLine className="h-4 w-4 text-forest-mid" /> Joint audit sign-off</p>
      {signed ? (
        <div className="mt-2 space-y-1 text-xs text-forest-dark">
          <p className="inline-flex items-center gap-1.5 rounded bg-emerald-100 px-2 py-0.5 font-semibold text-emerald-900"><Lock className="h-3 w-3" /> Signed off &amp; frozen</p>
          <p>Assessor: <b>{so.assessor_name}</b> · {String(so.assessor_at || "").slice(0, 16).replace("T", " ")}</p>
          <p>Contractor representative: <b>{so.contractor_name || "—"}</b></p>
          {so.comments && <p className="italic text-muted-foreground">“{so.comments}”</p>}
          {canReopen && <Button size="sm" variant="outline" className="mt-2 h-8 text-xs" onClick={reopen} data-testid={STANDARDS.lias.reopen}>Reopen to draft</Button>}
        </div>
      ) : canEdit ? (
        <div className="mt-3 space-y-2">
          <Input value={contractor} onChange={(e) => setContractor(e.target.value)} placeholder="Contractor representative name" className="h-8 text-sm" data-testid={STANDARDS.lias.contractorName} />
          <Textarea value={comments} onChange={(e) => setComments(e.target.value)} placeholder="Comments / observations (optional)" className="min-h-[60px] text-sm" data-testid={STANDARDS.lias.comments} />
          <Button size="sm" className="h-8 bg-forest-dark text-xs hover:bg-forest-mid" onClick={sign} data-testid={STANDARDS.lias.jointSignoff}>
            <CheckCircle2 className="mr-1 h-4 w-4" /> Sign off scorecard
          </Button>
          <p className="text-[11px] text-muted-foreground">Signing off freezes all scores and sign-offs for this assessment period.</p>
        </div>
      ) : (
        <p className="mt-2 text-xs text-muted-foreground">Draft assessment — awaiting assessor sign-off.</p>
      )}
    </div>
  );
}

export default function LiasScorecard({ periodId }) {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    if (!periodId) return;
    try {
      const r = await api.get(`/score/periods/${periodId}/lias`);
      setData(r.data);
    } catch (e) { toast.error(apiErr(e)); }
    setLoading(false);
  }, [periodId]);

  useEffect(() => { setLoading(true); load(); }, [load]);

  const download = () =>
    downloadFile(`/score/periods/${periodId}/lias/report.pdf`, `roots-lias-obc-${periodId}.pdf`)
      .catch(() => toast.error("PDF export failed"));

  if (loading || !data) return <p className="text-sm text-muted-foreground">Preparing LIAS scorecard…</p>;

  const sc = data.scorecard;
  const tone = GRADE_TONE[sc.grade_tone] || GRADE_TONE.warn;

  return (
    <div className="space-y-4" data-testid={STANDARDS.lias.panel}>
      {/* Header + grade */}
      <div className="rounded-xl border border-sage-light bg-sage-surface p-4">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0 space-y-0.5 text-xs text-forest-dark">
            <p className="flex items-center gap-1.5"><Building2 className="h-3.5 w-3.5 text-forest-mid" /> Service provider: <b>{data.header.service_provider}</b></p>
            <p>Property / estate: <b>{data.header.property_estate}</b></p>
            <p>Assessment period: <b>{data.header.period_label}</b></p>
            <p>
              Focus:{" "}
              <span data-testid={STANDARDS.lias.focus} className="rounded bg-white px-1.5 py-0.5 font-semibold capitalize text-forest-mid">{data.focus}</span>
              {data.focus_note && <span className="ml-1 text-[11px] text-muted-foreground">· {data.focus_note}</span>}
            </p>
          </div>
          <div className="text-right">
            <div className={`inline-flex items-center gap-2 rounded-xl border px-3 py-1.5 ${tone}`}>
              <span className="font-heading text-3xl font-bold" data-testid={STANDARDS.lias.grade}>{sc.grade || "—"}</span>
              <div className="text-left">
                <p className="text-[11px] font-semibold">{sc.grade_label}</p>
                <p className="font-heading text-lg font-bold" data-testid={STANDARDS.lias.finalPct}>{sc.final_pct == null ? "—" : `${sc.final_pct}%`}</p>
              </div>
            </div>
            <p className="mt-1 text-[10px] uppercase tracking-wider text-muted-foreground">final combined score</p>
          </div>
        </div>

        {/* Payment position */}
        <div className="mt-3 rounded-lg border border-sage-light bg-white p-3">
          <p className="flex items-center gap-1.5 text-xs font-semibold text-forest-dark"><Scale className="h-3.5 w-3.5 text-forest-mid" /> Indicative payment position — {sc.payment.headline}</p>
          <ul className="mt-1 space-y-0.5 text-[11px] text-muted-foreground">
            {sc.payment.lines.map((l, i) => <li key={i}>• {l}</li>)}
          </ul>
        </div>

        {data.termination_risk && (
          <div className="mt-3 flex items-start gap-2 rounded-lg border border-rose-300 bg-rose-50 p-3" data-testid={STANDARDS.lias.termination}>
            <ShieldAlert className="mt-0.5 h-4 w-4 shrink-0 text-rose-600" />
            <p className="text-xs text-rose-900"><b>Termination-risk flag:</b> two consecutive issued periods are Grade D. This is a flag only — no automated contract action is taken.</p>
          </div>
        )}
      </div>

      {data.can_config && (
        <ConfigEditor periodId={periodId} contractId={data.header.contract_id} config={data.config} onChange={load} />
      )}

      {/* Four weighted result areas */}
      {sc.areas.map((area) => (
        <div key={area.key} className="rounded-xl border border-sage-light p-3" data-testid={STANDARDS.lias.area(area.key)}>
          <div className="mb-2 flex items-center justify-between gap-2">
            <p className="font-heading text-sm font-bold text-forest-dark">{area.title}</p>
            <div className="flex items-center gap-2 text-xs">
              <span className="rounded-full bg-forest-mid px-2 py-0.5 font-semibold text-white">{area.weight}%</span>
              <span className={`font-heading text-lg font-bold ${scoreColor(area.score)}`}>{area.score == null ? "—" : `${area.score}/10`}</span>
            </div>
          </div>
          <div className="space-y-2">
            {area.kpis.map((k) => (
              <KpiRow key={k.key} periodId={periodId} areaKey={area.key} k={k} canEdit={data.can_edit} navigate={navigate} onChange={load} />
            ))}
          </div>
        </div>
      ))}

      <JointSignoff periodId={periodId} data={data} canEdit={data.can_edit} canReopen={data.can_reopen} onChange={load} />

      <div className="flex flex-wrap items-center justify-between gap-2 border-t border-sage-light pt-3">
        <p className="text-[11px] text-muted-foreground">
          Scores are auto-derived from the issued ROOTS evidence, then confirmed by an assessor. A performance &amp; readiness aid — ROOTS does not disburse fees.
        </p>
        <Button size="sm" variant="outline" onClick={download} data-testid={STANDARDS.lias.downloadPdf}><Download className="mr-1 h-4 w-4" /> Scorecard PDF</Button>
      </div>
    </div>
  );
}
